import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedPreferences } from '../../shared/preferences';
export { THEMES, isDarkTheme } from '../../shared/preferences';
export type { AppTheme } from '../../shared/preferences';
type Value = ReturnType<typeof useSharedPreferences>;
const Context = createContext<Value | undefined>(undefined);
export function AppThemeProvider({ children }: { children: ReactNode }) {
  const parent = useContext(Context);
  return parent ? <>{children}</> : <RootProvider>{children}</RootProvider>;
}
function RootProvider({ children }: { children: ReactNode }) {
  const value = useSharedPreferences();
  const { setLanguage } = value;
  const { i18n } = useTranslation();
  useEffect(() => { if (i18n.language !== value.language) void i18n.changeLanguage(value.language); }, [i18n, value.language]);
  useEffect(() => {
    const changed = (language: string) => setLanguage(language);
    i18n.on('languageChanged', changed);
    return () => { i18n.off('languageChanged', changed); };
  }, [i18n, setLanguage]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export const useOptionalAppTheme = () => useContext(Context) ?? null;
export function useAppTheme() {
  const value = useContext(Context);
  if (!value) throw new Error('useAppTheme must be used within an AppThemeProvider');
  return value;
}
